/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Shrink a picked image before it is uploaded.
 *
 * A photo straight off a phone is 3-5 MB and 4000px wide. Sent as-is it would
 * be a slow upload on cellular, a slow chat scroll for everyone else, and
 * megabytes of bytea per message. Nothing in a chat thumbnail needs more than
 * ~1600px, so the resize happens here rather than being enforced only by the
 * server's 2 MB rejection.
 */

/** Longest edge, in CSS pixels, of the uploaded image. */
const MAX_EDGE = 1600;
/** JPEG quality. 0.82 is where artefacts stop being visible on a photo. */
const QUALITY = 0.82;

export interface DownscaleResult {
  dataUrl: string;
  bytes: number;
}

/**
 * Decode with the orientation the camera recorded.
 *
 * A portrait iPhone photo is stored landscape with an EXIF rotation flag.
 * Drawing it to a canvas without honouring that flag produces a sideways
 * picture — createImageBitmap's imageOrientation is what applies it. The
 * <img> fallback is for browsers without that option, where the tag itself
 * already applies EXIF.
 */
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Fall through — Safari has thrown here on some HEIC-backed JPEGs.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("That image could not be read."));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downscaleImage(file: File): Promise<DownscaleResult> {
  if (!file.type.startsWith("image/")) {
    throw new Error("That file is not an image.");
  }

  const source = await decode(file);
  const sw = "width" in source ? source.width : 0;
  const sh = "height" in source ? source.height : 0;
  if (!sw || !sh) throw new Error("That image could not be read.");

  const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser cannot process images.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
  if ("close" in source) source.close();

  // Always JPEG: a photographed plate gains nothing from PNG and a PNG of the
  // same scene is several times larger. The server allow-lists jpeg/png/webp,
  // so this stays within what it will store.
  const dataUrl = canvas.toDataURL("image/jpeg", QUALITY);
  const comma = dataUrl.indexOf(",");
  const bytes = Math.floor((dataUrl.length - comma - 1) * 0.75);
  return { dataUrl, bytes };
}
